import React from "react";
import { View, StyleSheet } from "react-native";
import BaseController from "../../common/base/BaseController";
import settings from "../../config/settings";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import NtText from "../../components/NtText";
import NtBadge from "../../components/NtBadge";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtModal from "../../components/NtModal";
import NtUserExtensions from "../components/NtUserExtensions";
import NtUserRBAC from "../components/NtUserRBAC";
import NtUserUpdateForm from "../components/NtUserUpdateForm";
import { useTheme } from "../../context/ThemeContext";
import { fetchUser } from "../../api/user";
import UserContext from "../../context/UserContext";
import Logger from "../../common/utils/Logger";
import { showToast } from "../../common/utils/ControllerUtils";

// Hero card with avatar + identity + role + status. Hook component so we can
// read theme inside a class controller.
function IdentityHero({ user, onEdit }) {
  const { theme } = useTheme();
  const styles = makeHeroStyles(theme);
  const enabled = (user?.user_enabled ?? "true") === "true";
  const role = user?.role?.name || user?.group?.group_name;
  const initials = (user?.username || "?")
    .split(/[\s.@_-]+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <View style={styles.hero}>
      <View style={styles.avatar}>
        <NtText style={styles.avatarText}>{initials}</NtText>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <NtText style={styles.name} numberOfLines={1}>
          {user?.contact?.contact_name_given || user?.username || "—"}
        </NtText>
        <View style={styles.metaRow}>
          {user?.username ? (
            <NtText style={styles.metaText}>{user.username}</NtText>
          ) : null}
          {role ? (
            <NtBadge variant="info" dot={false}>
              {String(role).toUpperCase()}
            </NtBadge>
          ) : null}
          <NtBadge variant={enabled ? "green" : "gray"}>
            {enabled ? "Active" : "Disabled"}
          </NtBadge>
        </View>
      </View>
      <NtTouchableEffect style={styles.editBtn} onPress={onEdit}>
        <NtMaterialIcon name="pencil" color={theme.brand2} size={14} />
        <NtText style={styles.editText}>Edit profile</NtText>
      </NtTouchableEffect>
    </View>
  );
}

const makeHeroStyles = (t) =>
  StyleSheet.create({
    hero: {
      flexDirection: "row",
      alignItems: "center",
      gap: 16,
      backgroundColor: t.surface,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radiusCard,
      padding: 18,
    },
    avatar: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: t.brand,
      alignItems: "center",
      justifyContent: "center",
    },
    avatarText: { color: "#fff", fontWeight: "700", fontSize: 18 },
    name: {
      fontSize: 20,
      fontWeight: "700",
      color: t.text,
      letterSpacing: -0.3,
    },
    metaRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginTop: 4,
      flexWrap: "wrap",
    },
    metaText: { color: t.textDim, fontSize: 12 },
    editBtn: {
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: t.radius,
      borderWidth: 1,
      borderColor: t.borderSoft,
      backgroundColor: t.surface2,
    },
    editText: { color: t.text, fontSize: 12, fontWeight: "600" },
  });

function PageBody({ user, onEditProfile }) {
  const { theme } = useTheme();

  if (!user?.user_uuid) {
    return (
      <View style={{ padding: 18 }}>
        <NtText style={{ color: theme.textMute }}>Loading user…</NtText>
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <IdentityHero user={user} onEdit={onEditProfile} />
      <View style={{ flex: 1, gap: 16, marginTop: 14 }}>
        <NtUserExtensions userId={user.user_uuid} />
        <NtUserRBAC userId={user.user_uuid} />
      </View>
    </View>
  );
}

class UserDetailController extends BaseController {
  state = {
    ...super.state,
    user: null,
  };

  editModalRef = React.createRef();

  componentDidMount() {
    this.fetchUserData();
  }


  fetchUserData = async () => {
    const userId = this.props.route?.params?.id;
    if (!userId) return;
    const res = await fetchUser(this.context.user?.domain, userId);
    Logger("User detail fetch", res);
    if (res?.ok) this.setState({ user: res.data });
  };

  handleEditProfile = () => this.editModalRef.current?.show();

  handleProfileUpdated = () => {
    this.editModalRef.current?.dismiss();
    this.fetchUserData();
    showToast("Success", "User updated");
  };

  render() {
    const { user } = this.state;
    return (
      <NtRootPageContainer
        pageTitle={user?.contact?.contact_name_given || user?.username || "User"}
        pageSubtitle="Manage profile, extensions, roles, and permissions."
        showBackButton
        permission={this.handlePermissionCheck()}
      >
        <PageBody user={user} onEditProfile={this.handleEditProfile} />

        <NtModal ref={this.editModalRef}>
          <NtUserUpdateForm
            item={user}
            onCancel={() => this.editModalRef.current?.dismiss()}
            onUpdate={this.handleProfileUpdated}
          />
        </NtModal>
      </NtRootPageContainer>
    );
  }
}

export default UserDetailController;
